// MF-568 — Student × item grid shared by cards 'pat-response-guttman' and 'pat-response-matrix': sticky student
// column, header rows (strand band, item index, difficulty, strand initials), cell colour decided by the caller.
import { Box, Typography } from '@mui/material'
import { INK, FONT, SUMMARY_PALETTE } from '../boardTheme'
import { fmtD, itemTitle, stepIndex, strandInitials } from './patSkillsShared'

const CELL = 34
const STUDENT_W = 140

export const Legend = ({ entries }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
    {entries.map((e) => (
      <Box key={e.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            bgcolor: e.bg,
            border: `1px solid ${INK.axis}`,
            fontSize: 11,
            fontWeight: 700,
            color: e.fg || INK.primary,
            textAlign: 'center',
            lineHeight: '17px'
          }}
        >
          {e.symbol || ''}
        </Box>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>{e.label}</Typography>
      </Box>
    ))}
  </Box>
)

export const Note = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1, lineHeight: 1.5 }}>
    {children}
  </Typography>
)

const headCell = {
  position: 'sticky',
  bgcolor: '#fff',
  zIndex: 1,
  fontSize: 10.5,
  color: SUMMARY_PALETTE.muted,
  textAlign: 'center',
  fontWeight: 600,
  p: 0,
  fontFamily: FONT
}

/** cellStyle(student, item, response) → { bg, fg, title? }; stepLine draws the Guttman boundary per student. */
const PatSkillsGrid = ({ matrix, cellStyle, stepLine = false, bandByStrand = false }) => {
  const { items, students, strands, cell } = matrix
  return (
    <Box
      sx={{
        overflow: 'auto',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        border: `1px solid ${SUMMARY_PALETTE.cardBorder}`,
        borderRadius: '8px'
      }}
    >
      <Box component='table' sx={{ borderCollapse: 'separate', borderSpacing: 0, fontFamily: FONT }}>
        <thead>
          {bandByStrand ? (
            <tr>
              <Box component='th' sx={{ ...headCell, top: 0, left: 0, zIndex: 3, minWidth: STUDENT_W }} />
              {strands.map((s) => (
                <Box
                  key={`${s.strand}-${s.start}`}
                  component='th'
                  colSpan={s.span}
                  title={s.strand}
                  sx={{
                    ...headCell,
                    top: 0,
                    borderLeft: `1px solid ${INK.axis}`,
                    bgcolor: '#f3f6fa',
                    px: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                >
                  {s.span > 1 ? s.strand : strandInitials(s.strand)}
                </Box>
              ))}
            </tr>
          ) : null}
          <tr>
            <Box
              component='th'
              sx={{
                ...headCell,
                top: bandByStrand ? 20 : 0,
                left: 0,
                zIndex: 3,
                minWidth: STUDENT_W,
                textAlign: 'left',
                pl: 1,
                color: SUMMARY_PALETTE.ink
              }}
            >
              Student (score)
            </Box>
            {items.map((it) => (
              <Box
                key={it.uid}
                component='th'
                title={itemTitle(it)}
                sx={{ ...headCell, top: bandByStrand ? 20 : 0, minWidth: CELL, py: 0.25 }}
              >
                <Box sx={{ color: SUMMARY_PALETTE.ink }}>{it.idx}</Box>
                <Box>{fmtD(it.difficulty)}</Box>
                <Box sx={{ color: SUMMARY_PALETTE.faint }}>{strandInitials(it.strand)}</Box>
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {students.map((s) => {
            const step = stepLine ? stepIndex(items, s.score) : null
            return (
              <tr key={s.id}>
                <Box
                  component='th'
                  scope='row'
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: '#fff',
                    textAlign: 'left',
                    pl: 1,
                    pr: 1,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 500,
                    color: SUMMARY_PALETTE.ink,
                    whiteSpace: 'nowrap',
                    minWidth: STUDENT_W,
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {s.name}{' '}
                  <Box component='span' sx={{ color: SUMMARY_PALETTE.faint, fontWeight: 400 }}>
                    ({fmtD(s.score)})
                  </Box>
                </Box>
                {items.map((it, i) => {
                  const response = cell(s.id, it.uid)
                  const st = cellStyle(s, it, response)
                  const edge =
                    step === null
                      ? null
                      : i === step
                        ? 'inset -3px 0 0 ' + INK.primary
                        : step === -1 && i === 0
                          ? 'inset 3px 0 0 ' + INK.primary
                          : null
                  return (
                    <Box
                      key={it.uid}
                      component='td'
                      title={`${s.name} · item ${it.idx} (${it.uid}, difficulty ${fmtD(it.difficulty)}): ${st.title || response}`}
                      sx={{
                        width: CELL,
                        minWidth: CELL,
                        height: 28,
                        textAlign: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: st.bg,
                        color: st.fg,
                        border: `1px solid #fff`,
                        opacity: st.opacity || 1,
                        boxShadow: edge || 'none'
                      }}
                    >
                      {response}
                    </Box>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Box>
    </Box>
  )
}

export default PatSkillsGrid
